import React, { useRef, useState, useMemo, useCallback } from 'react';
import './Reservations.scss';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Card } from 'primereact/card';
import { PageTemplate } from '../../layout/PageTemplate/PageTemplate';
import { PrimeIcons } from 'primereact/api';
import { Reservation } from '../../types/reservation';
import { useOrganizationContext } from '../../context/OrganizationContext';
import { useAuthContext } from '../../context/AuthContext';
import { useReservationTypes } from '../../hooks/AppData/useReservationTypes';
import { ReservationPanel, ReservationEvent } from '../../components/ReservationPanel/ReservationPanel';
import { useResourcePanel } from '../../hooks/useResourcePanel';
import { Toast } from 'primereact/toast';
import { ReservationType } from '../../types/reservationType';
import { normalizeHiddenWeekdays } from '../../utils/calendarUtils';
import { useWindowSize } from '../../hooks/useWindowSize';
import { CalendarFilters } from '../../components/CalendarFilters/CalendarFilters';
import { hasFeaturesPermission } from '../../utils/featureUtils';
import classNames from 'classnames';
import { getCommonRestrictions, InitialHours } from './calendarRestrictions';
import { ReservationsCalendarSkeleton } from './ReservationsCalendarSkeleton';
import { ReservationsHeader } from './ReservationsHeader';
import { ReservationsCalendar } from './ReservationsCalendar';
import { useCalendarFullscreen } from './useCalendarFullscreen';
import { useReservationFilters } from './useReservationFilters';
import { ReservationsFilters, useReservationsData } from './useReservationsData';

export { getCommonRestrictions } from './calendarRestrictions';
export type { CalendarRestrictions, InitialHours } from './calendarRestrictions';

export const Reservations = () => {
  const toast = useRef<Toast>(null);
  const { user, userLoading } = useAuthContext();
  const { organization, organizationLoading, orgBranches } = useOrganizationContext();
  const configuredHiddenWeekdays = organization?.config?.reservationCalendar?.hiddenWeekdays;
  const hiddenWeekdays = useMemo(() => normalizeHiddenWeekdays(configuredHiddenWeekdays), [configuredHiddenWeekdays]);
  const { isMobile } = useWindowSize();

  const [createButtonReservationType, setCreateButtonReservationType] = useState<ReservationType | null>(null);
  const [initialHours, setInitialHours] = useState<InitialHours | undefined>();
  const [initialTemporaryEvent, setInitialTemporaryEvent] = useState<ReservationEvent | undefined>();
  const {
    reservationTypesLoading,
    reservationTypeFromId,
    uniqueReservationTypes: allUniqueReservationTypes,
    reservationTypes: allReservationTypes,
  } = useReservationTypes();
  const { features, featuresLoading } = useAuthContext();

  const {
    selectedBranchIds,
    setSelectedBranchIds,
    selectedEmployees,
    setSelectedEmployees,
    selectedReservationTypes,
    setSelectedReservationTypes,
    clearFilters,
    hasAppliedFilters,
  } = useReservationFilters({
    userId: user?.id,
    organizationId: organization?.id,
    userLoading,
    reservationTypesLoading,
    allReservationTypes,
  });

  const uniqueReservationTypes = useMemo(() => {
    return allUniqueReservationTypes.filter((type) =>
      hasFeaturesPermission([type.features.manage], { features, featuresLoading }),
    );
  }, [allUniqueReservationTypes, features, featuresLoading]);
  const reservationTypes = useMemo(() => {
    return allReservationTypes?.filter((type) => hasFeaturesPermission([type.features.manage], { features, featuresLoading }));
  }, [allReservationTypes, features, featuresLoading]);

  const reservationsFilters = useMemo(
    (): ReservationsFilters => ({
      branchIds: selectedBranchIds,
      employeeIds: selectedEmployees.map((emp) => emp.id),
      // Expand each selected unique type to include all branch variants with the same name,
      // so filtering by type doesn't hide reservations from other branches.
      reservationTypeIds: selectedReservationTypes.flatMap(
        (selectedType) =>
          allReservationTypes?.filter((type) => type.name === selectedType.name).map((type) => type.id) ?? [selectedType.id],
      ),
    }),
    [selectedBranchIds, selectedEmployees, selectedReservationTypes, allReservationTypes],
  );

  const {
    data,
    reservationsLoading,
    mutateReservations,
    currentView,
    currentDate,
    isDebouncedNavigationLoading,
    handleRangeChange,
    handleNavigate,
    handleViewChange,
    handleToolbarNavigate,
    handleToolbarView,
  } = useReservationsData({ userId: user?.id, isMobile, hiddenWeekdays, filters: reservationsFilters });

  const { isEditing, setIsEditing, panelVisible, closePanel, selectedResource, openPanel, isDeleting } = useResourcePanel(data);

  const createButtonItems = useMemo(() => {
    return uniqueReservationTypes.map((type) => ({
      label: type.name,
      icon: PrimeIcons.PLUS,
      command: () => {
        setCreateButtonReservationType(type);
      },
    }));
  }, [uniqueReservationTypes]);

  const handleCreateButtonClick = () => {
    if (!createButtonReservationType) {
      setCreateButtonReservationType(uniqueReservationTypes[0]);
    }
    openPanel(false);
  };

  const onView = useCallback(
    (reservation: Reservation | undefined) => {
      if (reservation) openPanel(false, reservation.id);
    },
    [openPanel],
  );

  // Los filtros ahora se aplican en el backend
  const events = useMemo(() => data ?? [], [data]);

  const calendarRestrictions = useMemo(() => {
    // Para las restricciones del calendario, usamos el primer tipo de reserva seleccionado o ninguna restricción específica
    const selectedType = selectedReservationTypes.length > 0 ? selectedReservationTypes[0] : null;
    // Para las sucursales, usamos la primera sucursal seleccionada o ninguna restricción específica
    const selectedBranchId = selectedBranchIds.length > 0 ? selectedBranchIds[0] : null;

    return getCommonRestrictions(reservationTypes, selectedBranchId, selectedType);
  }, [reservationTypes, selectedBranchIds, selectedReservationTypes]);

  const onSelectNewSlot = ({
    initialHours: newInitialHours,
    temporaryEvent,
  }: {
    initialHours: InitialHours;
    temporaryEvent: ReservationEvent;
  }) => {
    setInitialHours(newInitialHours);
    setInitialTemporaryEvent(temporaryEvent);
    openPanel(false);
  };

  const handleClose = () => {
    setInitialHours(undefined);
    setInitialTemporaryEvent(undefined);
    closePanel();
  };

  const hasMultipleBranches = (orgBranches?.length ?? 0) > 1;

  const calendarLoading =
    userLoading || organizationLoading || reservationTypesLoading || reservationsLoading || isDebouncedNavigationLoading;

  const { isFullscreen, toggleFullscreen, fullscreenContainerRef } = useCalendarFullscreen({ panelVisible, calendarLoading });

  return (
    <PageTemplate className='reservation-page' title='Reservas'>
      <Toast ref={toast} />
      <Card
        className='reservation-card'
        header={
          <ReservationsHeader
            reservationTypesLoading={reservationTypesLoading}
            uniqueReservationTypes={uniqueReservationTypes}
            createButtonReservationType={createButtonReservationType}
            onCreate={handleCreateButtonClick}
            createButtonItems={createButtonItems}
          />
        }
      >
        <CalendarFilters
          selectedBranchIds={selectedBranchIds}
          setSelectedBranchIds={setSelectedBranchIds}
          selectedEmployees={selectedEmployees}
          setSelectedEmployees={setSelectedEmployees}
          selectedReservationTypes={selectedReservationTypes}
          setSelectedReservationTypes={setSelectedReservationTypes}
          reservationTypes={uniqueReservationTypes}
          hasAppliedFilters={hasAppliedFilters}
          clearFilters={clearFilters}
        />

        <div
          ref={fullscreenContainerRef}
          className={classNames('calendar-fullscreen-container', { 'is-fullscreen': isFullscreen })}
        >
          {calendarLoading ? (
            <ReservationsCalendarSkeleton
              currentDate={currentDate}
              currentView={currentView}
              onNavigate={handleToolbarNavigate}
              onView={handleToolbarView}
              isFullscreen={isFullscreen}
              onToggleFullscreen={toggleFullscreen}
              hiddenWeekdays={hiddenWeekdays}
            />
          ) : (
            <ReservationsCalendar
              events={events}
              currentDate={currentDate}
              currentView={currentView}
              onNavigate={handleNavigate}
              onView={handleViewChange}
              onRangeChange={handleRangeChange}
              hiddenWeekdays={hiddenWeekdays}
              calendarRestrictions={calendarRestrictions}
              reservationTypeFromId={reservationTypeFromId}
              hasMultipleBranches={hasMultipleBranches}
              createButtonReservationType={createButtonReservationType}
              setCreateButtonReservationType={setCreateButtonReservationType}
              uniqueReservationTypes={uniqueReservationTypes}
              isFullscreen={isFullscreen}
              onToggleFullscreen={toggleFullscreen}
              onOpenReservation={onView}
              onSelectNewSlot={onSelectNewSlot}
            />
          )}
        </div>
      </Card>
      <ReservationPanel
        resource={selectedResource}
        visible={panelVisible}
        onHide={handleClose}
        onIsEditingChange={setIsEditing}
        clearInitialTemporaryEvent={() => setInitialTemporaryEvent(undefined)}
        isDeleting={isDeleting}
        isEditing={isEditing}
        createModeReservationTypeId={createButtonReservationType?.id}
        initialHours={initialHours}
        initialTempEventOverride={initialTemporaryEvent}
        preselectedBranchId={selectedBranchIds.length === 1 ? selectedBranchIds[0] : undefined}
        mutateReservations={mutateReservations}
        calendarRestrictions={calendarRestrictions}
      />
    </PageTemplate>
  );
};
